<script>
    import {onMount} from 'svelte';

    export let startTime = '';
    export let endTime = '';
    export let title = '';
    export let englishTitle = '';
    export let types = '';
    export let typeId = '';
    export let wytyczne_id = [];
    export let id = '';
    export let chairman = '';
    export let type = '';

    // Must match the row height in timetable.svelte (.content: 60px per 10-minute step).
    const PX_PER_MINUTE = 6;
    const ROW_MINUTES = 10;
    const MAX_HOUR = 24;

    let isHovered = false;
    let isOverflowing = false;
    let eventBox;
    let bodyBox;

    const toMinutes = (time) => {
        const [hours, minutes] = time.trim().split(':').map(Number);
        return hours * 60 + minutes;
    };

    const calculateDuration = (time) => {
        const [start, end] = time.split('-');
        if (!start || !end) return ROW_MINUTES;
        return toMinutes(end) - toMinutes(start);
    };

    // timetable.svelte draws a session in the row whose step is nearest to its start
    // (isExactMatch, ±5 min), so shift the tile by the remaining minutes.
    const calculateOffset = (time) => {
        const minuteInRow = toMinutes(time.split('-')[0]) % ROW_MINUTES;
        const shift = minuteInRow < ROW_MINUTES / 2 ? minuteInRow : minuteInRow - ROW_MINUTES;
        return shift * PX_PER_MINUTE;
    };

    const findObjectByKey = (types, id) => {
        for (const key in types) {
            if (types.hasOwnProperty(key) && key === id) {
                return {[key]: types[key]};
            }
        }
        return null;
    };

    $: newObj = findObjectByKey(types, typeId);
    $: typeLabel = type || (newObj ? newObj[typeId]?.name[0] : '');
    $: duration = calculateDuration(startTime);
    $: baseHeight = Math.max(duration * PX_PER_MINUTE - 4, 40);
    $: top = calculateOffset(startTime) + 2;
    $: timeLabel = startTime.split('-').map(t => t.trim()).join(' – ');
    $: zIndex = MAX_HOUR - parseInt(startTime.split(':')[0], 10);
    $: checkEventId = wytyczne_id && id ? wytyczne_id.includes(id.toString()) : false;
    $: isExpanded = isHovered && isOverflowing;

    const checkOverflow = () => {
        if (eventBox) {
            isOverflowing = eventBox.scrollHeight > eventBox.clientHeight + 1
                || (bodyBox && bodyBox.scrollHeight > bodyBox.clientHeight + 1);
        }
    };

    onMount(() => {
        checkOverflow();
    });
</script>

<div bind:this={eventBox}
     class="event-tile"
     class:expanded={isExpanded}
     class:overflowing={isOverflowing && !isExpanded}
     on:mouseenter={() => { isHovered = true; checkOverflow(); }}
     on:mouseleave={() => { isHovered = false; checkOverflow(); }}
     style="top: {top}px; z-index: {isExpanded ? 60 : zIndex}; {isExpanded ? `min-height: ${baseHeight}px` : `height: ${baseHeight}px`};">
    <div class="event-meta">
        <span class="event-time">{timeLabel}</span>
        {#if checkEventId}
            <span class="event-badge">WYTYCZNE</span>
        {/if}
    </div>
    <div class="event-body" bind:this={bodyBox}>
        <p class="event-title">{@html title}</p>
        {#if englishTitle}
            <p class="event-title-en">{englishTitle}</p>
        {/if}
        <span class="event-spacer"></span>
    </div>
    {#if typeLabel}
        <div class="event-footer">
            <span class="event-type">{typeLabel}</span>
        </div>
    {/if}
</div>

<style>
    .event-tile {
        position: absolute;
        left: 6px;
        right: 6px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 6px 10px 8px 12px;
        overflow: hidden;
        text-align: left;
        background: linear-gradient(180deg, #ffffff 0%, #eef4fd 100%);
        border: 1px solid #e6e6e6;
        border-left: 4px solid #2189a6;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
        transition: box-shadow 0.15s ease, border-color 0.15s ease;
    }

    .event-tile:hover,
    .event-tile.expanded {
        border-color: #bdbdbd;
        border-left-color: #2189a6;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
    }

    /* Fade out text that does not fit; the tile expands on hover. */
    .event-tile.overflowing .event-body::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 18px;
        background: linear-gradient(180deg, rgba(238, 244, 253, 0) 0%, #eef4fd 90%);
        pointer-events: none;
    }

    .event-meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 8px;
        line-height: 16px;
    }

    .event-time {
        font-size: 12px;
        font-weight: 700;
        color: #3f3e3c;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .event-type,
    .event-badge {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        line-height: 16px;
        padding: 0 6px;
        border-radius: 999px;
        white-space: nowrap;
    }

    .event-badge {
        color: white;
        background: #d2222c;
    }

    .event-body {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        text-align: center;
    }

    /* Spacers centre the title vertically but collapse when it overflows, so it never clips at the top. */
    .event-body::before,
    .event-spacer {
        content: '';
        flex: 1 1 0;
    }

    .event-footer {
        flex: none;
        display: flex;
        justify-content: center;
        margin: 0 -10px -8px -12px;
        padding: 5px 8px 6px;
        background: rgb(226 236 254);
        border-top: 1px solid rgba(33, 137, 166, 0.15);
    }

    .event-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
        color: #3f3e3c;
    }

    .event-title-en {
        margin: 0;
        font-size: 12px;
        font-style: italic;
        line-height: 1.3;
        color: #3f3e3c;
    }

    @media (min-width: 1000px) {
        .event-title {
            font-size: 15px;
        }
    }

    @media print {
        .event-tile {
            box-shadow: none;
        }
    }
</style>
